/**
 * Helpers for keeping a user's preferred-device selection pinned to the same physical
 * device across a browser media re-enumeration.
 *
 * The media store resolves the active microphone/speaker/camera by walking a stack of
 * preferred deviceIds and returning the first one that is currently enumerable. When a
 * freshly-selected device is re-enumerated with a new deviceId (e.g. a headset switching
 * USB audio profile when its call-control interface is opened), the old id becomes invalid
 * and the resolver would otherwise silently fall through to a previously-preferred device.
 * Remapping the preferred ids on a device change prevents that fall-through.
 */

import type { MediaDevice } from '@/stores/MediaDevices.types'

/**
 * True if two enumerated devices describe the same physical endpoint.
 *
 * Within a single page session Chrome keeps a stable groupId per physical device, so that
 * is the primary signal. The USB product id (parsed from the label) and the label itself
 * are used as fallbacks in case the groupId also changed. Kind must always match so a
 * microphone is never mistaken for its paired speaker.
 * @param a First device
 * @param b Second device
 */
export function isSamePhysicalDevice(a: MediaDevice, b: MediaDevice): boolean {
	if (a.kind !== b.kind) { return false }
	if (a.groupId && b.groupId && a.groupId === b.groupId) { return true }
	if (a.productId && b.productId && a.productId === b.productId) { return true }
	if (a.label && b.label && a.label === b.label) { return true }
	return false
}

/**
 * Rewrite a list of preferred deviceIds so any id that was just removed is replaced by the
 * id of the same physical device if it re-appeared under a new id. Ids that were genuinely
 * disconnected (no matching re-added device) are left untouched. A device is never remapped
 * onto a different physical device, so the user's selection can never silently jump to
 * another headset.
 * @param preferred Ordered list of preferred deviceIds (front = most recently selected)
 * @param removed Devices that disappeared in this enumeration
 * @param added Devices that appeared in this enumeration
 */
export function remapPreferredDeviceIds(preferred: string[], removed: MediaDevice[], added: MediaDevice[]): string[] {
	if (removed.length === 0 || added.length === 0) { return preferred }

	const claimed = new Set<string>()

	return preferred.map(id => {
		const goneDevice = removed.find(device => device.deviceId === id)
		if (!goneDevice) { return id }

		const replacement = added.find(device => !claimed.has(device.deviceId) && isSamePhysicalDevice(goneDevice, device))
		if (!replacement) { return id }

		claimed.add(replacement.deviceId)
		return replacement.deviceId
	})
}
